import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { getDisclaimerPack, type Jurisdiction } from "./jurisdiction";

let _admin: any = null;
function getAdmin() {
  if (!_admin) {
    _admin = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_SERVICE_ROLE_KEY!,
    );
  }
  return _admin;
}

type Locale = "fr" | "en" | "nl";

const InputSchema = z.object({
  locale: z.enum(["fr", "en", "nl"]).default("fr"),
  jurisdiction: z
    .enum(["EU_STRICT", "UK", "CH_NO", "US", "CA", "AU_NZ", "DEFAULT"])
    .default("DEFAULT"),
}).optional();

const GOAL_LABELS: Record<Locale, Record<string, string>> = {
  fr: { skin: "Peau & Éclat", weight: "Forme physique", muscle: "Tonus musculaire", antiaging: "Anti-âge & Longévité" },
  en: { skin: "Skin & Glow", weight: "Fitness", muscle: "Muscle tone", antiaging: "Anti-ageing & Longevity" },
  nl: { skin: "Huid & glans", weight: "Fitheid", muscle: "Spiertonus", antiaging: "Anti-veroudering & levensduur" },
};

type Metric = { label: string; score: number; delta: string };
type Tip = {
  title: string;
  detail: string;
  category?: string;
  horizon?: string;
  frequency?: string;
  disclaimer?: string;
};
type Analysis = {
  created_at: string;
  goal: string;
  summary: string | null;
  score: number | null;
  metrics: Metric[] | null;
  tips: Tip[] | null;
  user_notes: string | null;
  user_actions: { kind: string; label: string }[] | null;
};

const CATEGORY_LABEL: Record<Locale, Record<string, string>> = {
  fr: { habit: "Habitude", nutrition: "Nutrition", supplement: "Supplément", posture: "Posture", sleep: "Sommeil", skincare: "Soin de la peau", hydration: "Hydratation", exercise: "Exercice", mindset: "Mental" },
  en: { habit: "Habit", nutrition: "Nutrition", supplement: "Supplement", posture: "Posture", sleep: "Sleep", skincare: "Skincare", hydration: "Hydration", exercise: "Exercise", mindset: "Mindset" },
  nl: { habit: "Gewoonte", nutrition: "Voeding", supplement: "Supplement", posture: "Houding", sleep: "Slaap", skincare: "Huidverzorging", hydration: "Hydratatie", exercise: "Oefening", mindset: "Mindset" },
};
const HORIZON_LABEL: Record<Locale, Record<string, string>> = {
  fr: { daily: "Quotidien", weekly: "Hebdo", "4-weeks": "4 semaines", "12-weeks": "12 semaines" },
  en: { daily: "Daily", weekly: "Weekly", "4-weeks": "4 weeks", "12-weeks": "12 weeks" },
  nl: { daily: "Dagelijks", weekly: "Wekelijks", "4-weeks": "4 weken", "12-weeks": "12 weken" },
};

const PDF_TEXT: Record<Locale, Record<string, string>> = {
  fr: { report: "Rapport Avant / Après", progress: "Votre progression IA", generated: "Généré le", goal: "Objectif", before: "AVANT", after: "APRÈS", current: "ACTUEL", summary: "Synthèse IA", metrics: "Indicateurs actuels", tips: "Vos {0} conseils personnalisés", weekly: "Progression semaine par semaine", date: "DATE", score: "SCORE", resume: "RÉSUMÉ", empty: "Aucune analyse disponible. Lancez une analyse avant de générer le rapport.", footer: "Glow Up Coach - Coach photo IA bienveillant. Données traitées en UE. Aucun diagnostic médical.", filename: "glow-up-coach-rapport", taken_on: "Photo prise le", your_notes: "Vos remarques", your_actions: "Vos actions déclarées" },
  en: { report: "Before / After report", progress: "Your AI progress", generated: "Generated on", goal: "Goal", before: "BEFORE", after: "AFTER", current: "CURRENT", summary: "AI summary", metrics: "Current indicators", tips: "Your {0} personalised tips", weekly: "Week-by-week progress", date: "DATE", score: "SCORE", resume: "SUMMARY", empty: "No analysis available. Run an analysis before generating the report.", footer: "Glow Up Coach - Caring AI photo coach. Data processed in the EU. No medical diagnosis.", filename: "glow-up-coach-report", taken_on: "Photo taken on", your_notes: "Your notes", your_actions: "Your declared actions" },
  nl: { report: "Voor/na-rapport", progress: "Jouw AI-voortgang", generated: "Gegenereerd op", goal: "Doel", before: "VOOR", after: "NA", current: "ACTUEEL", summary: "AI-samenvatting", metrics: "Huidige indicatoren", tips: "Jouw {0} persoonlijke tips", weekly: "Voortgang week per week", date: "DATUM", score: "SCORE", resume: "SAMENVATTING", empty: "Geen analyse beschikbaar. Voer een analyse uit voordat je het rapport genereert.", footer: "Glow Up Coach - Zorgzame AI-fotocoach. Gegevens verwerkt in de EU. Geen medische diagnose.", filename: "glow-up-coach-rapport", taken_on: "Foto gemaakt op", your_notes: "Jouw notities", your_actions: "Jouw aangegeven acties" },
};

function wrap(text: string, max: number): string[] {
  const words = (text ?? "").split(/\s+/);
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > max) {
      if (cur) lines.push(cur);
      cur = w;
    } else {
      cur = (cur + " " + w).trim();
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

// Replace characters StandardFonts (WinAnsi) cannot encode.
function sanitize(s: string): string {
  return (s ?? "")
    .replace(/[\u2018\u2019\u02BC]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/\u00A0/g, " ")
    .replace(/[^\x00-\xFF]/g, "");
}

export const generateReportPdf = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => InputSchema.parse(input) ?? { locale: "fr" as const, jurisdiction: "DEFAULT" as const })
  .handler(async ({ data, context }) => {
    const locale = data.locale;
    const jurisdiction = (data.jurisdiction ?? "DEFAULT") as Jurisdiction;
    const pack = getDisclaimerPack(jurisdiction, locale);
    const localeTag = locale === "fr" ? "fr-FR" : locale === "nl" ? "nl-NL" : "en-GB";
    const { userId } = context;
    const admin = getAdmin();

    const { data: rows, error } = await admin
      .from("analyses")
      .select("created_at, goal, summary, score, metrics, tips, user_notes, user_actions")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(12);

    if (error) throw new Error(error.message);
    const analyses = (rows ?? []) as Analysis[];
    if (analyses.length === 0) {
      throw new Error(PDF_TEXT[locale].empty);
    }

    const latest = analyses[0];
    const oldest = analyses[analyses.length - 1];
    const history = analyses.slice().reverse(); // chronological for the chart

    const pdf = await PDFDocument.create();
    const font = await pdf.embedFont(StandardFonts.Helvetica);
    const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

    const ink = rgb(0.06, 0.06, 0.08);
    const mute = rgb(0.45, 0.45, 0.5);
    const accent = rgb(0.95, 0.42, 0.22);
    const line = rgb(0.88, 0.88, 0.9);

    let page = pdf.addPage([595.28, 841.89]); // A4
    const W = page.getWidth();
    const H = page.getHeight();
    const M = 48;
    let y = H - M;
    const newPage = () => {
      page = pdf.addPage([595.28, 841.89]);
      y = H - M;
    };
    const ensure = (needed: number) => {
      if (y - needed < M + 24) newPage();
    };

    // ── Header
    page.drawText("GLOW UP COACH", { x: M, y, size: 10, font: bold, color: accent });
    page.drawText(sanitize(PDF_TEXT[locale].report), {
      x: W - M - bold.widthOfTextAtSize(sanitize(PDF_TEXT[locale].report), 10),
      y,
      size: 10,
      font: bold,
      color: mute,
    });
    y -= 28;
    page.drawText(sanitize(PDF_TEXT[locale].progress), { x: M, y, size: 26, font: bold, color: ink });
    y -= 14;
    const dateStr = new Date().toLocaleDateString(localeTag, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    page.drawText(
      sanitize(`${PDF_TEXT[locale].generated} ${dateStr} - ${PDF_TEXT[locale].goal}: ${GOAL_LABELS[locale][latest.goal] ?? latest.goal}`),
      { x: M, y, size: 10, font, color: mute },
    );
    y -= 24;
    page.drawLine({ start: { x: M, y }, end: { x: W - M, y }, thickness: 0.6, color: line });
    y -= 24;

    // ── Before / After cards
    const cardW = (W - M * 2 - 16) / 2;
    const cardH = 110;
    const drawCard = (x: number, label: string, a: Analysis) => {
      page.drawRectangle({
        x, y: y - cardH, width: cardW, height: cardH,
        borderColor: line, borderWidth: 0.8, color: rgb(0.985, 0.98, 0.97),
      });
      page.drawText(sanitize(label), { x: x + 14, y: y - 22, size: 9, font: bold, color: accent });
      const d = new Date(a.created_at).toLocaleDateString(localeTag, {
        day: "2-digit", month: "short", year: "numeric",
      });
      page.drawText(sanitize(d), { x: x + 14, y: y - 40, size: 13, font: bold, color: ink });
      const sum = wrap(sanitize(a.summary ?? "—"), 48).slice(0, 3);
      let yy = y - 60;
      for (const ln of sum) {
        page.drawText(ln, { x: x + 14, y: yy, size: 9.5, font, color: ink });
        yy -= 13;
      }
    };
    drawCard(M, PDF_TEXT[locale].before, oldest);
    drawCard(M + cardW + 16, analyses.length > 1 ? PDF_TEXT[locale].after : PDF_TEXT[locale].current, latest);
    y -= cardH + 28;

    // ── AI summary
    page.drawText(sanitize(PDF_TEXT[locale].summary), { x: M, y, size: 13, font: bold, color: ink });
    y -= 16;
    const takenStr = new Date(latest.created_at).toLocaleString(localeTag, {
      day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    page.drawText(sanitize(`${PDF_TEXT[locale].taken_on}: ${takenStr}`), {
      x: M, y, size: 9, font, color: mute,
    });
    y -= 14;
    for (const ln of wrap(sanitize(latest.summary ?? "—"), 95)) {
      page.drawText(ln, { x: M, y, size: 10.5, font, color: ink });
      y -= 14;
    }
    y -= 14;

    // ── User-declared notes and actions
    if (latest.user_actions && latest.user_actions.length > 0) {
      ensure(28);
      page.drawText(sanitize(PDF_TEXT[locale].your_actions), { x: M, y, size: 11, font: bold, color: ink });
      y -= 14;
      const actionsStr = latest.user_actions.map((a) => `- ${a.label}`).join("  ");
      for (const ln of wrap(sanitize(actionsStr), 95)) {
        page.drawText(ln, { x: M, y, size: 9.5, font, color: ink });
        y -= 12;
      }
      y -= 6;
    }
    if (latest.user_notes && latest.user_notes.length > 0) {
      ensure(28);
      page.drawText(sanitize(PDF_TEXT[locale].your_notes), { x: M, y, size: 11, font: bold, color: ink });
      y -= 14;
      for (const ln of wrap(sanitize(latest.user_notes), 95)) {
        page.drawText(ln, { x: M, y, size: 9.5, font, color: ink });
        y -= 12;
      }
      y -= 6;
    }

    // ── Metrics (latest)
    if (latest.metrics?.length) {
      page.drawText(sanitize(PDF_TEXT[locale].metrics), { x: M, y, size: 13, font: bold, color: ink });
      y -= 18;
      for (const m of latest.metrics) {
        page.drawText(sanitize(m.label), { x: M, y, size: 10, font: bold, color: ink });
        page.drawText(sanitize(m.delta ?? ""), {
          x: W - M - font.widthOfTextAtSize(sanitize(m.delta ?? ""), 10),
          y, size: 10, font, color: accent,
        });
        y -= 10;
        const barW = W - M * 2;
        page.drawRectangle({ x: M, y: y - 4, width: barW, height: 4, color: rgb(0.93, 0.93, 0.95) });
        page.drawRectangle({
          x: M, y: y - 4, width: Math.max(0, Math.min(100, m.score) / 100) * barW,
          height: 4, color: accent,
        });
        y -= 18;
      }
      y -= 6;
    }

    // ── All tips with metadata
    if (latest.tips?.length) {
      ensure(28);
      page.drawText(
        sanitize(PDF_TEXT[locale].tips.replace("{0}", String(latest.tips.length))),
        { x: M, y, size: 13, font: bold, color: ink },
      );
      y -= 18;
      latest.tips.forEach((t, i) => {
        const detailLines = wrap(sanitize(t.detail ?? ""), 90);
        const discLines = t.disclaimer ? wrap(sanitize(`! ${t.disclaimer}`), 95) : [];
        const blockHeight =
          14 /* title */ +
          (t.category || t.horizon || t.frequency ? 12 : 0) +
          detailLines.length * 12 +
          (discLines.length ? 4 + discLines.length * 11 : 0) +
          10;
        ensure(blockHeight);
        page.drawText(`${String(i + 1).padStart(2, "0")}`, {
          x: M, y, size: 14, font: bold, color: accent,
        });
        page.drawText(sanitize(t.title ?? ""), { x: M + 26, y, size: 11, font: bold, color: ink });
        y -= 14;
        const tags: string[] = [];
        if (t.category) tags.push(CATEGORY_LABEL[locale][t.category] ?? t.category);
        if (t.horizon) tags.push(HORIZON_LABEL[locale][t.horizon] ?? t.horizon);
        if (t.frequency) tags.push(t.frequency);
        if (tags.length) {
          page.drawText(sanitize(tags.join("  -  ")), {
            x: M + 26, y, size: 8.5, font: bold, color: accent,
          });
          y -= 12;
        }
        for (const ln of detailLines) {
          page.drawText(ln, { x: M + 26, y, size: 9.5, font, color: mute });
          y -= 12;
        }
        if (discLines.length) {
          y -= 4;
          for (const ln of discLines) {
            page.drawText(ln, { x: M + 26, y, size: 8.5, font, color: accent });
            y -= 11;
          }
        }
        y -= 10;
      });
      y -= 6;
    }

    // ── Week-by-week comparison table
    ensure(40);
    page.drawText(sanitize(PDF_TEXT[locale].weekly), { x: M, y, size: 13, font: bold, color: ink });
    y -= 16;
    page.drawLine({ start: { x: M, y }, end: { x: W - M, y }, thickness: 0.5, color: line });
    y -= 14;
    page.drawText(sanitize(PDF_TEXT[locale].date), { x: M, y, size: 8, font: bold, color: mute });
    page.drawText(sanitize(PDF_TEXT[locale].goal.toUpperCase()), { x: M + 110, y, size: 8, font: bold, color: mute });
    page.drawText(sanitize(PDF_TEXT[locale].score), { x: M + 240, y, size: 8, font: bold, color: mute });
    page.drawText(sanitize(PDF_TEXT[locale].resume), { x: M + 300, y, size: 8, font: bold, color: mute });
    y -= 10;
    page.drawLine({ start: { x: M, y }, end: { x: W - M, y }, thickness: 0.5, color: line });
    y -= 12;

    for (const a of history) {
      ensure(18);
      const d = new Date(a.created_at).toLocaleDateString(localeTag, {
        day: "2-digit", month: "2-digit", year: "2-digit",
      });
      page.drawText(d, { x: M, y, size: 9.5, font, color: ink });
      page.drawText(sanitize(GOAL_LABELS[locale][a.goal] ?? a.goal), {
        x: M + 110, y, size: 9.5, font, color: ink,
      });
      page.drawText(a.score != null ? String(a.score) : "—", {
        x: M + 240, y, size: 9.5, font: bold, color: accent,
      });
      const summaryShort = wrap(sanitize(a.summary ?? "—"), 40)[0] ?? "";
      page.drawText(summaryShort, { x: M + 300, y, size: 9.5, font, color: mute });
      y -= 14;
    }

    // ── Footer
    const footY = 30;
    page.drawLine({
      start: { x: M, y: footY + 14 }, end: { x: W - M, y: footY + 14 },
      thickness: 0.5, color: line,
    });
    page.drawText(
      sanitize(pack.footer),
      { x: M, y: footY, size: 7.5, font, color: mute },
    );

    const bytes = await pdf.save();
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    const base64 = btoa(bin);
    return { base64, filename: `${PDF_TEXT[locale].filename}-${new Date().toISOString().slice(0, 10)}.pdf` };
  });